import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  deleteDraft,
  getDb,
  getDraft,
  readableOutbox,
  readStoreCache,
  saveDraft,
  type OutboxRecord,
  type StoredStoreCache,
} from '@/offline'
import { projectStore, type StoreState } from './projection'
import { STORE_COMMAND_TYPES } from './commands'

export interface UseStoreState {
  loading: boolean
  /** null when no store cache has been downloaded yet. */
  state: StoreState | null
  stored: StoredStoreCache | null
}

const EMPTY: OutboxRecord[] = []

/** The projected store (cache + this phone's own F5 commands + their sync results), live. */
export function useStoreState(): UseStoreState {
  const stored = useLiveQuery(async () => (await readStoreCache()) ?? null, [], undefined)
  const outbox = useLiveQuery(
    async () => {
      const db = getDb()
      return readableOutbox(
        await db.outbox
          .where('type')
          .anyOf([...STORE_COMMAND_TYPES])
          .toArray(),
        db,
      )
    },
    [],
    EMPTY,
  )
  const state = useMemo(() => (stored ? projectStore(stored, outbox) : null), [stored, outbox])
  return { loading: stored === undefined, state, stored: stored ?? null }
}

/** Current time, refreshed every `ms` — for ages and expiry (render stays pure). */
export function useNow(ms = 60_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(id)
  }, [ms])
  return now
}

/**
 * Flow state kept as a draft in IndexedDB (survives reloads and a dead battery) until the flow
 * commits. Returns [value, update, loaded, clear].
 */
export function useDraftState<T>(
  draftId: string,
  kind: string,
  initial: () => T,
  actorId: string | null,
) {
  const [value, setValue] = useState<T>(initial)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    let alive = true
    void getDraft<T>(draftId).then((d) => {
      if (!alive) return
      if (d) setValue(d.data)
      setLoaded(true)
    })
    return () => {
      alive = false
    }
  }, [draftId])
  const update = useCallback(
    (fn: (prev: T) => T) => {
      setValue((prev) => {
        const next = fn(prev)
        void saveDraft({ id: draftId, kind, actor_id: actorId, session_id: null, data: next })
        return next
      })
    },
    [draftId, kind, actorId],
  )
  const clear = useCallback(async () => {
    await deleteDraft(draftId)
    setValue(initial())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId])
  return [value, update, loaded, clear] as const
}
